import { useQueries } from "@tanstack/react-query";
import { useMemo } from "react";
import { useWallets } from "./useWallets";
import { getWalletBalanceV2 } from "../api";
import { enrichWalletAssets } from "../enrich-wallet-assets";
import type { PortfolioSummary } from "../insights/types";
import type { Wallet, WalletBalanceItem } from "../types";

export type EnrichedWallet = Wallet & {
  assets: WalletBalanceItem[];
  nativeBalance: number;
  nativeSymbol: string;
  irrValue: number;
  isLoading: boolean;
  isError: boolean;
  /** The backend's summary of this network (its 24h / 7d change); null until it arrives. */
  portfolio: PortfolioSummary | null;
};

export { enrichWalletAssets };

/**
 * How often a visible page re-reads the balance. Money can arrive with no event on the socket (a transfer from
 * someone else, a gateway deposit settling), so the header and the wallet follow it without a reload. Paused while
 * the tab is hidden (React Query's default), and re-read the moment the user comes back to it. Any socket event of
 * the user's re-reads it at once (`BalanceWatch`).
 */
export const BALANCE_REFRESH_MS = 45_000;

export function useUserBalance() {
  const { data: walletsData, isLoading: isWalletsLoading } = useWallets();

  const wallets: Wallet[] = walletsData?.data?.wallets ?? [];

  const balanceQueries = useQueries({
    queries: wallets.map((wallet) => ({
      // Address AND network id: the same address on two networks (the normal
      // case for EVM-compatible chains) must not share a cache entry — see
      // `getWalletBalanceV2`'s note on the doubling this endpoint replaces.
      queryKey: ["wallet-balance", wallet.address, wallet.network.id],
      queryFn: () => getWalletBalanceV2(wallet.address, wallet.network.id),
      staleTime: 1000 * 30,
      refetchInterval: BALANCE_REFRESH_MS,
      refetchOnWindowFocus: true,
      enabled: !!wallet.address && !!wallet.network?.id,
    })),
  });

  const isLoading =
    isWalletsLoading ||
    balanceQueries.some((q) => q.isLoading && q.fetchStatus !== "idle");

  const enrichedWallets = useMemo<EnrichedWallet[]>(() => {
    return wallets.map((wallet, index) => {
      const query = balanceQueries[index];
      const allAssets: WalletBalanceItem[] = query?.data?.data?.balance ?? [];

      return {
        ...wallet,
        ...enrichWalletAssets(wallet, allAssets),
        isLoading: query?.isLoading ?? false,
        isError: query?.isError ?? false,
        portfolio: query?.data?.data?.portfolio ?? null,
      };
    });
  }, [wallets, balanceQueries]);

  const totalIrr = useMemo(
    () => enrichedWallets.reduce((acc, w) => acc + w.irrValue, 0),
    [enrichedWallets]
  );

  return {
    wallets: enrichedWallets,
    totalIrr,
    isLoading,
    refetch: () => balanceQueries.forEach((q) => q.refetch()),
  };
}
